import { useEffect } from 'react';
import { create } from 'zustand';
import type { Book, LibrarySource } from '../types';
import { coverBlob, trackMedia } from '../lib/mediaInfo';
import { applyDuration, rememberDuration } from '../lib/durations';
import { useApp } from './app';

/**
 * Cover art found inside audio files, for books with no image beside them in
 * Drive — the usual case for a lone .m4b or a tagged MP3.
 *
 * Read lazily, only for books actually on screen, two at a time; each file is
 * read once ever, since results are kept (lib/mediaInfo.ts). The same read
 * yields the file's exact length where the container records it, which is
 * passed on to the duration cache for free.
 */

interface CoverState {
  /** Book id -> object URL of its embedded cover. */
  urls: Record<string, string>;
}

export const useCovers = create<CoverState>(() => ({ urls: {} }));

const CONCURRENCY = 2;
const RETRY_AFTER_MS = 60_000;

const queue: { book: Book; source: LibrarySource }[] = [];
const requested = new Set<string>();
let active = 0;

export function requestCover(book: Book, source: LibrarySource): void {
  if (book.coverUrl || !source.readRange || requested.has(book.id)) return;
  requested.add(book.id);
  queue.push({ book, source });
  pump();
}

function pump(): void {
  while (active < CONCURRENCY) {
    const next = queue.shift();
    if (!next) return;
    active += 1;
    void extract(next.book, next.source).finally(() => {
      active -= 1;
      pump();
    });
  }
}

async function extract(book: Book, source: LibrarySource): Promise<void> {
  const track = book.tracks[0];
  if (!track || !source.readRange) return;

  const media = await trackMedia(track, (start, end) => source.readRange!(track, start, end));
  if (!media) {
    // Could not be read right now; allow another attempt later.
    setTimeout(() => requested.delete(book.id), RETRY_AFTER_MS);
    return;
  }

  const seconds = media.durationSec;
  if (seconds && !track.durationSec) {
    void rememberDuration(track, seconds);
    useApp.setState((s) => ({ books: applyDuration(s.books, track.id, seconds) }));
  }

  if (!media.hasCover) return;
  const blob = await coverBlob(track);
  if (!blob) return;
  const url = URL.createObjectURL(blob);
  useCovers.setState((s) => ({ urls: { ...s.urls, [book.id]: url } }));
}

/** The cover to show for a book: an image beside it in Drive, or else the art
 *  inside its first file, once read. Asks for that read when first shown. */
export function useCoverUrl(book: Book | null | undefined): string | undefined {
  const embedded = useCovers((s) => (book ? s.urls[book.id] : undefined));
  const source = useApp((s) => s.source);

  useEffect(() => {
    if (book && source) requestCover(book, source);
  }, [book, source]);

  return book?.coverUrl ?? embedded;
}

/** Same, outside React — for the lock-screen artwork. */
export function coverUrlOf(book: Book): string | undefined {
  return book.coverUrl ?? useCovers.getState().urls[book.id];
}
